'use client';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Container,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';

import { FAQSection as FAQSectionType } from '../../../services/types';

interface FAQSectionProps {
  data?: FAQSectionType;
}

export default function FAQSection({ data }: FAQSectionProps) {
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);

  // Set the first FAQ as expanded by default if any FAQ is marked as expanded
  useEffect(() => {
    if (data?.faqs && data.faqs.length > 0) {
      const defaultExpanded = data.faqs.find(faq => faq.isExpanded);
      if (defaultExpanded) {
        setExpandedFAQ(defaultExpanded.id);
      }
    }
  }, [data?.faqs]);

  if (!data) {
    return null;
  }

  const handleFAQToggle = (faqId: number) => {
    setExpandedFAQ(expandedFAQ === faqId ? null : faqId);
  };

  return (
    <Box
      component='section'
      sx={{
        py: { xs: 6, md: 10 },
        backgroundColor: 'white',
      }}
    >
      <Container maxWidth='md'>
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: { xs: 6, md: 8 } }}>
          <Typography
            variant='h2'
            component='h2'
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: { xs: '28px', md: '36px' },
              lineHeight: 1.53,
              color: '#001A44',
              mb: 3,
              textTransform: 'uppercase',
            }}
          >
            {data.title}
          </Typography>
        </Box>

        {/* FAQ Items */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {data.faqs && data.faqs.length > 0 ? (
            data.faqs.map(faq => (
              <Accordion
                key={faq.id}
                expanded={expandedFAQ === faq.id}
                onChange={() => handleFAQToggle(faq.id)}
                elevation={0}
                sx={{
                  border: '1px solid #DEDEDE',
                  borderRadius: '0px !important',
                  backgroundColor: 'white',
                  '&:before': {
                    display: 'none',
                  },
                  '&.Mui-expanded': {
                    margin: 0,
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <ExpandMoreIcon
                      sx={{
                        transform:
                          expandedFAQ === faq.id
                            ? 'rotate(180deg)'
                            : 'rotate(0deg)',
                        transition: 'transform 0.3s ease',
                        color: '#02073E',
                      }}
                    />
                  }
                  sx={{
                    px: 3,
                    py: 2,
                    minHeight: '70px',
                    backgroundColor: 'white',
                    '&.Mui-expanded': {
                      minHeight: '70px',
                    },
                    '& .MuiAccordionSummary-content': {
                      margin: 0,
                      '&.Mui-expanded': {
                        margin: 0,
                      },
                    },
                  }}
                >
                  <Typography
                    variant='h6'
                    component='h3'
                    sx={{
                      fontFamily: 'Lato, sans-serif',
                      fontWeight: 700,
                      fontSize: '16px',
                      lineHeight: 1.75,
                      flex: 1,
                      pr: 2,
                    }}
                  >
                    {faq.question}
                  </Typography>
                </AccordionSummary>

                <AccordionDetails
                  sx={{
                    px: 3,
                    backgroundColor: 'white',
                  }}
                >
                  <Typography
                    variant='body1'
                    sx={{
                      fontFamily: 'Lato, sans-serif',
                      fontWeight: 400,
                      fontSize: '16px',
                      lineHeight: 1.75,
                      color: '#6E6F72',
                      opacity: 0.7,
                    }}
                  >
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))
          ) : (
            <Typography
              variant='body1'
              sx={{
                textAlign: 'center',
                color: '#6c757d',
                fontStyle: 'italic',
              }}
            >
              Không có câu hỏi thường gặp nào
            </Typography>
          )}
        </Box>
      </Container>
    </Box>
  );
}
